import React, { useState, useRef, useEffect } from "react";
import pkg from "../../../package.json";
import { STATS_EVENT_TYPES } from "zstats";
import { Widget, WidgetType } from "zrender";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getDasUrl } from "actions/config";
import { getUserData } from "actions/user";
import { getSelectedHotel } from "actions/hotel";
import { getRoom } from "modules/rooms/store";
import useLandingSession from "hooks/useLandingSession";
import { useStats } from "contexts/stats";
import { resolveStatsTag } from "utils/statistics";
import useWindowDimensions, { getDeviceInfo } from "./DesignUtils";
import { updateVODStatus } from "actions/hotel";
import { checkValidURL } from "utils/url";

const ACTIVE_STATUS = "active";
const DEFAULT_STATUS = "default";
const FOCUS_STATUS = "focus";
const Status = [ACTIVE_STATUS, DEFAULT_STATUS, FOCUS_STATUS];

const WidgetHandler = ({ data, type, lang, getLink, promos, parentElement }) => {
    const widgetRef = useRef();
    const { t } = useTranslation();
    const history = useHistory();
    const { theme, project, locationVOD } = useLandingSession();
    const { id, style, data: widgetDataOriginal, actions, statsTag, widgetID } = data;

    const [widgetType, setWidgetType] = useState(type);
    const [widgetData, setWidgetData] = useState(widgetDataOriginal);
    const [DAS, setDAS] = useState(getDasUrl());
    const selectedHotel = useSelector(getSelectedHotel);
    const user = useSelector(getUserData);
    const room = useSelector(getRoom);
    const VODReady = useSelector((state) => (state.cache.VODReady && project ? state.cache.VODReady[project] : null));
    const dispatch = useDispatch();
    const stats = useStats();

    const { height: WindowHeight, width: WindowWidth } = useWindowDimensions();
    const {
        type: deviceType,
        width,
        height,
    } = getDeviceInfo({
        width: WindowWidth > 512 ? 512 : WindowWidth,
        height: WindowHeight,
    });

    function commonNavigation(statsTag, link, action) {
        if (link?.type === "library") {
            stats.addEvent({
                type: STATS_EVENT_TYPES.SECTION,
                eventName: "library",
            });
        }
        if (statsTag) {
            stats.addEvent({ eventName: statsTag, type: STATS_EVENT_TYPES.CLICK });
        }
        if (action.type == "content" || action.value == "landing") {
            if (link?.onClick) {
                link?.onClick();
            }
        } else {
            if (link?.onClick) {
                link?.onClick();
            }
            if (link?.href) {
                if (link?.target) {
                    window.open(link.href, "_blank");
                } else {
                    history.push(link.href.replace("/#", ""));
                }
            }
        }
    }

    function parseData(wData, type) {
        const composedData = { ...wData, ...wData?.statistics };
        switch (type) {
            case WidgetType.HTML:
                return { ...composedData, code: wData?.code?.text };
            case WidgetType.Button:
            case WidgetType.Messages:
            case WidgetType.ChatButton:
                return { ...composedData, title: wData?.title?.text };
            case WidgetType.Text:
                return { ...composedData, ...wData?.image };
            case WidgetType.Image:
                return { ...composedData, ...wData?.src };
            case WidgetType.Logo:
                return {
                    ...composedData,
                    libraryRef: wData?.src?.libraryRef || wData?.logo?.src?.libraryRef,
                    externalUrl: wData?.src?.externalUrl || wData?.logo?.src?.externalUrl,
                    adjust: "fill",
                };
            case WidgetType.Slider:
                return {
                    ...composedData,
                    items: wData?.items?.map((item) => ({
                        ...item,
                        externalUrl: item?.resource?.externalUrl,
                        libraryRef: item?.resource?.libraryRef,
                        text: item?.text?.text,
                    })),
                };
            case WidgetType.Promo:
                const promosByIds = promos?.promosByIds || [];
                const promosDefault = promos?.promosDefault || [];
                const promoByDefault = promosDefault?.[0] || null;
                const promoId = wData?.promoId || null;
                const promoById = promosByIds.find((p) => String(p?.id) === String(promoId)) || null;
                return { ...composedData, promos: [promoByDefault, promoById] };
            case WidgetType.Video:
                return { justThumbnail: true, ...composedData };
            default:
                return composedData;
        }
    }

    function parseTheme({ theme, parentElement }) {
        function parseFonts(font) {
            return {
                ...font,
                font: font.font.name,
                resource: font.font,
                textAlign: font.alignment,
            };
        }
        const themeParsed = Status.reduce((acc, curr) => {
            const bg =
                parentElement === WidgetType.Container && curr == DEFAULT_STATUS
                    ? "rgba(255,255,255,0)"
                    : theme?.colors[`${curr}Background`];
            const fg = theme?.colors[`${curr}Foreground`];
            acc[curr] = {
                rgbaBG: bg,
                bg,
                rgbaFG: fg,
                fg,
            };
            return acc;
        }, {});

        themeParsed.headerStyle = parseFonts(theme.fonts.heading);
        themeParsed.paragraphStyle = parseFonts(theme.fonts.paragraph);
        return themeParsed;
    }

    function parseEvents(type) {
        switch (type) {
            case WidgetType.ChatButton:
                return {
                    onClick: () => {
                        const showChat = pkg.features.chat && selectedHotel && selectedHotel.chatURL;
                        const canUseChat =
                            showChat && ((selectedHotel && selectedHotel.chatPublic) || (user && user.emailVerified));
                        if (canUseChat) {
                            if (stats) {
                                stats.addEvent({
                                    type: STATS_EVENT_TYPES.SECTION,
                                    eventName: "Chat Reception",
                                });
                            }
                            window.open(selectedHotel.chatURL, "_blank");
                        } else {
                            if (selectedHotel && selectedHotel.chatURL) {
                                if (user && !user.emailVerified) {
                                    toast(t("must verify to chat"));
                                } else {
                                    toast(t("must login to chat"));
                                }
                            } else {
                                toast(t("No linked phone number"));
                            }
                        }
                    },
                };
            case WidgetType.Messages:
                return {
                    onClick: () => {
                        if (selectedHotel?.ref && room) {
                            commonNavigation(statsTag, { href: "/messages" }, { type: "section", value: "messages" });
                        } else {
                            toast(t("have a room?"));
                        }
                    },
                };
            case WidgetType.Slider:
                return {
                    onEnter: (response) => {
                        const customStatTags = resolveStatsTag(
                            response?.statistics,
                            `${widgetID}_${response?.id}_slider_interface`,
                        );
                        const link = getLink({ ...widgetData, actions: [response.action] });
                        commonNavigation(customStatTags, link, response.action);
                    },
                };
            case WidgetType.Button:
            case WidgetType.Image:
            case WidgetType.Icon:
                return {
                    onClick: (action) => {
                        const link = getLink({ ...widgetData, actions: [action] });
                        commonNavigation(statsTag, link, action);
                    },
                };
            case WidgetType.Promo:
                return {
                    onClick: (info) => {
                        const customStatTags = resolveStatsTag(
                            info?.action,
                            `${info?.contentId}_PromotionalContent_Advertising`,
                        );
                        const link = getLink({ ...widgetData, actions: [info?.action] });
                        commonNavigation(customStatTags, link, info.action);
                    },
                };

            default:
                return {};
        }
    }

    function parseActions(actions, type) {
        switch (type) {
            case WidgetType.ChatButton:
                return [{ type: "chat" }];
            case WidgetType.Messages:
                return [{ type: "messages" }];
            default:
                return actions;
        }
    }

    function onVODError() {
        if (widgetData.alternativeContent.libraryRef || widgetData.alternativeContent.useVideoFrame) {
            if (widgetData.alternativeContent.useVideoFrame) {
                setWidgetData((prev) => ({
                    ...prev,
                    src: prev?.video?.[0],
                    kind: "cover",
                    adjust: "contain",
                    justThumbnail: true,
                }));
            } else if (widgetData.alternativeContent.libraryRef) {
                setWidgetData((prevData) => ({ ...prevData, src: widgetData.alternativeContent, adjust: "contain" }));
                setWidgetType(WidgetType.Image);
            }
        } else {
            setWidgetData((prevData) => ({
                ...prevData,
                video: [widgetData.alternativeContent],
            }));
        }
    }

    useEffect(() => {
        if (widgetType == WidgetType.Video) {
            if (widgetData?.video?.[0]?.libraryRef) {
                if (VODReady != null) {
                    const cacheTime = new Date().getTime() - VODReady.date;
                    const cacheMax = 60000; // 1 min
                    if (cacheTime <= cacheMax) {
                        if (!VODReady.ready) {
                            onVODError();
                            return;
                        }
                    }
                }

                checkValidURL(locationVOD, 0, 500)
                    .then((data) => {
                        let status = {};
                        status[project] = true;
                        setWidgetData((prev) => ({
                            ...prev,
                            justThumbnail: false,
                        }));
                        setDAS(locationVOD);
                        dispatch(updateVODStatus(status));
                    })
                    .catch((err) => {
                        let status = {};
                        status[project] = false;
                        dispatch(updateVODStatus(status));
                        onVODError();
                    });
            }
        }
    }, [widgetType]);

    return (
        <Widget
            ref={widgetRef}
            id={id}
            style={style}
            data={parseData(widgetData, widgetType)}
            type={widgetType}
            deviceType={deviceType}
            DAS={DAS}
            lang={lang}
            theme={parseTheme({ theme, parentElement })}
            widgetSize={{
                width,
                height,
            }}
            screenSize={{
                width,
                height,
            }}
            events={parseEvents(widgetType)}
            actions={parseActions(actions, widgetType)}
        />
    );
};

export default WidgetHandler;
